import { ReactElement } from 'react';
import PropTypes from 'prop-types';
import TableCell from '@mui/material/TableCell';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SafetyCheckIcon from '@mui/icons-material/SafetyCheck';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import ShieldIcon from '@mui/icons-material/Shield';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { prettyDateFormat } from 'utils';
import Tooltip from '@mui/material/Tooltip';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { STATUS_TYPES } from 'pages/AnnualUserAccessReview/_const';

/**
 * Factory component to display contents in a table contextually
 * @param {object[]} props.headCells table definition
 * @param {string} props.id value type
 * @param {object} props.row data for the current row
 * @param {string} props.labelId identifier for the checkbox
 * @param {string} props.searchString if present, output text gets highlighted where it matches this string
 * @returns {ReactElement} a single cell in the Paging Table
 */
const PersonnelTableCell = ({
	headCells,
	id,
	row,
	labelId,
	searchString = '',
}) => {
	const getStatusIcon = type => {
		switch (type) {
			case STATUS_TYPES.OVERDUE:
				return <GppMaybeIcon sx={{ color: 'red' }} />;
			case STATUS_TYPES.VALID:
				return <VerifiedUserIcon sx={{ color: 'green' }} />;
			case STATUS_TYPES.VALID_EXPIRING_SOON:
				return <SafetyCheckIcon sx={{ color: 'orange' }} />;
			default:
				return (
					<Skeleton>
						<ShieldIcon />
					</Skeleton>
				);
		}
	};
	const getStatusLabel = data => {
		switch (data.status) {
			case STATUS_TYPES.OVERDUE:
				return (
					'Overdue.  Expired on ' +
					prettyDateFormat(data.dateValidationExpires) +
					'.'
				);
			case STATUS_TYPES.VALID:
				return (
					'Valid until ' +
					prettyDateFormat(data.dateValidationExpires) +
					'.'
				);
			case STATUS_TYPES.VALID_EXPIRING_SOON:
				return (
					'Valid. Expires in ' + data.daysUntilExpiration + ' days.'
				);
			default:
				return null;
		}
	};

	const highlightSearchResults = value => {
		const matches = match(value, searchString, { insideWords: true });
		const parts = parse(value, matches);
		const highlightedValue = parts.map((part, index) => (
			<span
				key={index}
				style={{
					fontWeight: part.highlight ? 700 : 400,
				}}
			>
				{part.text}
			</span>
		));
		return highlightedValue;
	};

	const test = headCells.some(h => h.id === id);
	let Cell = null;

	switch (id) {
		case 'dateValidationExpires':
			Cell = (
				<TableCell>
					<Stack direction='row' alignItems='center' gap={1}>
						{getStatusIcon(row.status)}
						{getStatusLabel(row)}
					</Stack>
				</TableCell>
			);
			break;
		case 'department':
			Cell = (
				<TableCell>{highlightSearchResults(row.department)}</TableCell>
			);
			break;
		case 'jobTitle':
			Cell = (
				<TableCell>{highlightSearchResults(row.jobTitle)}</TableCell>
			);
			break;
		case 'name':
			Cell = (
				<TableCell
					component='th'
					id={labelId}
					scope='row'
					padding='none'
				>
					{(
						<Stack direction='row' alignItems='center'>
							{highlightSearchResults(row.name)}
							{row.isDelegated && (
								<Tooltip
									title={
										row.delegatedByName
											? `Delegated to you by ${row.delegatedByName}`
											: 'Delegated to you'
									}
									placement='right-end'
									sx={{ ml: 1 }}
								>
									<TransferWithinAStationIcon />
								</Tooltip>
							)}
						</Stack>
					) ?? <Skeleton />}
				</TableCell>
			);
			break;
		case 'companyName':
			Cell = (
				<TableCell>
					{row.companyName
						? highlightSearchResults(row.companyName)
						: ''}
				</TableCell>
			);
			break;
		case 'delegatedReviewerName':
			Cell = (
				<TableCell>
					{row.delegatedReviewerName ? (
						<Stack direction='row' alignItems='center' gap={1}>
							<Tooltip
								title={
									row.delegatedReviewerMessage ||
									'Delegated reviewer'
								}
								placement='right-end'
							>
								<InfoOutlinedIcon
									fontSize='small'
									sx={{ color: 'blue' }}
								/>
							</Tooltip>
							<span>
								{highlightSearchResults(
									row.delegatedReviewerName,
								)}
							</span>
						</Stack>
					) : (
						''
					)}
				</TableCell>
			);
			break;
		default:
			break;
	}

	return test ? Cell : null;
};

PersonnelTableCell.propTypes = {
	headCells: PropTypes.arrayOf(PropTypes.object).isRequired,
	id: PropTypes.string.isRequired,
	row: PropTypes.object.isRequired,
	labelId: PropTypes.string,
};

export default PersonnelTableCell;
